import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { useContent } from "@/content";
import { postNotify, postContact } from "@/lib/api";
import { EASE } from "@/components/Motion";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

const Field = ({ id, label, error, textarea, ...rest }) => (
  <div>
    <label htmlFor={id} className="text-[0.75rem] uppercase tracking-[0.25em] opacity-70">{label}</label>
    {textarea ? (
      <textarea id={id} rows={4} className="field resize-none" aria-invalid={!!error} {...rest} />
    ) : (
      <input id={id} className="field" aria-invalid={!!error} {...rest} />
    )}
    {error && <p role="alert" data-testid={`${id}-error`} className="mt-2 text-xs text-copper">{error}</p>}
  </div>
);

const Honeypot = ({ value, onChange }) => (
  <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
    <label>Website<input type="text" name="website" tabIndex={-1} autoComplete="off" value={value} onChange={onChange} data-testid="honeypot-field" /></label>
  </div>
);

const Success = ({ text, testid }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }} className="flex items-start gap-5 border border-copper/50 p-7" role="status" data-testid={testid}>
    <Check size={26} strokeWidth={1} className="mt-1 shrink-0 text-copper" />
    <p className="font-serif text-xl leading-snug">{text}</p>
  </motion.div>
);

const SubmitBtn = ({ loading, children, testid }) => (
  <button type="submit" disabled={loading} data-testid={testid} className="group inline-flex items-center gap-3 rounded-[5px] bg-copper px-8 py-4 text-[0.75rem] uppercase tracking-[0.3em] text-white transition-opacity duration-300 hover:opacity-90 disabled:opacity-85">
    {children}
    <ArrowRight size={16} strokeWidth={1.4} className="transition-transform duration-500 group-hover:translate-x-1" />
  </button>
);

const useForm = (initial, validate, send) => {
  const [v, setV] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(v);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("loading");
    try { await send(v); setStatus("done"); } catch { setStatus("error"); }
  };
  return { v, set, errors, status, submit };
};

export const NotifyForm = ({ className = "" }) => {
  const f = useContent().forms;
  const { notify: t, errors: er } = f;
  const { v, set, errors, status, submit } = useForm(
    { email: "", brand: "", website: "" },
    (v) => (!v.email.trim() ? { email: er.required } : !EMAIL_RE.test(v.email.trim()) ? { email: er.email } : {}),
    postNotify,
  );
  return (
    <div className={className} data-testid="notify-form-wrapper">
      <AnimatePresence mode="wait">
        {status === "done" ? (
          <Success key="ok" text={t.success} testid="notify-success" />
        ) : (
          <motion.form key="form" exit={{ opacity: 0 }} onSubmit={submit} noValidate className="relative space-y-7" data-testid="notify-form">
            <Field id="notify-email" type="email" label={t.email} value={v.email} onChange={set("email")} error={errors.email} autoComplete="email" data-testid="notify-email-input" />
            <Field id="notify-brand" type="text" label={t.brand} value={v.brand} onChange={set("brand")} autoComplete="organization" data-testid="notify-brand-input" />
            <Honeypot value={v.website} onChange={set("website")} />
            {status === "error" && <p role="alert" className="text-sm text-copper" data-testid="notify-error">{er.server}</p>}
            <SubmitBtn loading={status === "loading"} testid="notify-submit">{status === "loading" ? t.sending : t.submit}</SubmitBtn>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export const ContactForm = ({ className = "" }) => {
  const f = useContent().forms;
  const { contact: t, errors: er } = f;
  const { v, set, errors, status, submit } = useForm(
    { name: "", email: "", phone: "", brand: "", message: "", website: "" },
    (v) => {
      const e = {};
      if (!v.name.trim()) e.name = er.required;
      if (!v.email.trim()) e.email = er.required;
      else if (!EMAIL_RE.test(v.email.trim())) e.email = er.email;
      if (!v.message.trim()) e.message = er.required;
      return e;
    },
    postContact,
  );
  return (
    <div className={className} data-testid="contact-form-wrapper">
      <AnimatePresence mode="wait">
        {status === "done" ? (
          <Success key="ok" text={t.success} testid="contact-success" />
        ) : (
          <motion.form key="form" exit={{ opacity: 0 }} onSubmit={submit} noValidate className="relative space-y-7" data-testid="contact-form">
            <div className="grid gap-7 md:grid-cols-2">
              <Field id="contact-name" label={t.name} value={v.name} onChange={set("name")} error={errors.name} autoComplete="name" data-testid="contact-name-input" />
              <Field id="contact-email" type="email" label={t.email} value={v.email} onChange={set("email")} error={errors.email} autoComplete="email" data-testid="contact-email-input" />
              <Field id="contact-phone" type="tel" label={t.phone} value={v.phone} onChange={set("phone")} autoComplete="tel" data-testid="contact-phone-input" />
              <Field id="contact-brand" label={t.brand} value={v.brand} onChange={set("brand")} autoComplete="organization" data-testid="contact-brand-input" />
            </div>
            <Field id="contact-message" textarea label={t.message} value={v.message} onChange={set("message")} error={errors.message} data-testid="contact-message-input" />
            <Honeypot value={v.website} onChange={set("website")} />
            {status === "error" && <p role="alert" className="text-sm text-copper" data-testid="contact-error">{er.server}</p>}
            <SubmitBtn loading={status === "loading"} testid="submit-contact">{status === "loading" ? t.sending : t.submit}</SubmitBtn>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
